import { useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { useAuth } from "../../lib/auth-context";
import { useCollection } from "../../lib/firestore-hooks";
import { formatDateOfBirth, formatRut } from "../../lib/format";
import type { PasajeroDoc } from "../../lib/types";

interface PasajeroRow extends PasajeroDoc {
  id: string;
}

/**
 * /admin/passengers — passenger directory. Doc id is auto-generated;
 * mostly read-only browsing with edit/soft-delete. Inactive passengers
 * are hidden from the Solicitar typeahead but still readable here.
 */
export function AdminPasajeros() {
  const { usuario } = useAuth();
  const canEdit = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
  const { rows } = useCollection<PasajeroDoc>("Pasajeros", orderBy("fullName"));
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<PasajeroRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const term = filter.trim().toLowerCase();
    return rows
      .filter((r) => showInactive || r.active)
      .filter(
        (r) =>
          !term ||
          r.fullName.toLowerCase().includes(term) ||
          (r.family?.toLowerCase().includes(term) ?? false) ||
          (r.rut?.toLowerCase().includes(term) ?? false)
      );
  }, [rows, filter, showInactive]);

  if (!filtered) return <p className="text-sm text-slate-500">Cargando…</p>;
  const inactiveCount = rows?.filter((r) => !r.active).length ?? 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Pasajeros</h1>
          <p className="mt-1 text-sm text-slate-600">
            Directorio de pasajeros. {rows?.length ?? 0} en total
            {inactiveCount ? `, ${inactiveCount} inactivo${inactiveCount === 1 ? "" : "s"}` : ""}.
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
          >
            + Nuevo pasajero
          </button>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar nombre, familia o RUT…"
          className="block w-full max-w-md rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Incluir inactivos
        </label>
      </div>

      {/* Mobile cards. Desktop table on md+. */}
      <ul className="space-y-2 md:hidden">
        {filtered.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
            Sin resultados.
          </li>
        ) : (
          filtered.map((p) => (
            <li
              key={p.id}
              onClick={canEdit ? () => setEditing(p) : undefined}
              className={
                (canEdit ? "cursor-pointer " : "") +
                "rounded-lg border p-3 text-sm " +
                (p.active
                  ? "border-slate-200 bg-white"
                  : "border-slate-200 bg-slate-50 text-slate-500")
              }
            >
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-900">
                    {p.fullName}
                  </div>
                  {p.family ? (
                    <div className="text-xs text-slate-600">{p.family}</div>
                  ) : null}
                </div>
                {!p.active ? (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                    inactivo
                  </span>
                ) : null}
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-xs text-slate-600">
                <div>RUT · {p.rut ? formatRut(p.rut) : "—"}</div>
                <div>nac · {p.dateOfBirth ? formatDateOfBirth(p.dateOfBirth) : "—"}</div>
              </div>
            </li>
          ))
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Familia</th>
              <th className="px-3 py-2">RUT</th>
              <th className="px-3 py-2">Fecha nac.</th>
              <th className="px-3 py-2 text-center">Activo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <tr
                key={p.id}
                onClick={canEdit ? () => setEditing(p) : undefined}
                className={
                  (canEdit ? "cursor-pointer " : "") +
                  (p.active ? "hover:bg-sky-50" : "bg-slate-50 text-slate-500 hover:bg-brand-50")
                }
              >
                <td className="px-3 py-2 text-slate-900">{p.fullName}</td>
                <td className="px-3 py-2 text-slate-700">{p.family ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">
                  {p.rut ? formatRut(p.rut) : "—"}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">
                  {p.dateOfBirth ? formatDateOfBirth(p.dateOfBirth) : "—"}
                </td>
                <td className="px-3 py-2 text-center">{p.active ? "✓" : "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">
                  Sin resultados.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editing ? (
        <PasajeroEditor initial={editing} onClose={() => setEditing(null)} />
      ) : null}
      {creating ? (
        <PasajeroEditor initial={null} onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

function PasajeroEditor({
  initial,
  onClose,
}: {
  initial: PasajeroRow | null;
  onClose: () => void;
}) {
  const isNew = initial === null;
  const [fullName, setFullName] = useState(initial?.fullName ?? "");
  const [family, setFamily] = useState(initial?.family ?? "");
  const [rut, setRut] = useState(initial?.rut ?? "");
  const [dob, setDob] = useState(initial?.dateOfBirth ?? "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function del() {
    if (isNew || !initial) return;
    setError(null);
    setSaving(true);
    try {
      await deleteDoc(doc(db, "Pasajeros", initial.id));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  async function save() {
    setError(null);
    if (!fullName.trim()) return setError("Nombre obligatorio.");
    setSaving(true);
    try {
      const data = {
        fullName: fullName.trim(),
        family: family.trim() || null,
        rut: rut.trim() ? formatRut(rut.trim()) : null,
        dateOfBirth: dob.trim() || null,
        active,
      };
      if (isNew) {
        await addDoc(collection(db, "Pasajeros"), {
          ...data,
          createdAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, "Pasajeros", initial!.id), data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          {isNew ? "Nuevo pasajero" : "Editar pasajero"}
        </h2>
        {confirmDelete ? (
          <>
            <div className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
              ¿Eliminar a <strong>{initial?.fullName}</strong>? Esta acción
              no se puede deshacer.
            </div>
            {error ? (
              <p className="mt-3 rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                {error}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setConfirmDelete(false)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={del}
                className="rounded-md bg-rose-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
              >
                {saving ? "Eliminando…" : "Eliminar definitivamente"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 space-y-4">
              <LabeledInput label="Nombre completo" value={fullName} onChange={setFullName} />
              <LabeledInput label="Familia" value={family} onChange={setFamily} />
              <LabeledInput
                label="RUT"
                value={rut}
                onChange={setRut}
                onBlur={() => setRut(formatRut(rut))}
                mono
              />
              <LabeledInput
                label="Fecha de nacimiento (YYYY-MM-DD)"
                value={dob}
                onChange={setDob}
                mono
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Activo
              </label>
              {error ? (
                <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                  {error}
                </p>
              ) : null}
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              {!isNew ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                >
                  Eliminar
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={onClose}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={save}
                  className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  onBlur,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  mono?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={
          "mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 " +
          (mono ? "font-mono" : "")
        }
      />
    </div>
  );
}
